import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import moment from "moment";
import {
  Card,
  Row,
  Col,
  Divider,
  Space,
  Typography,
  Spin,
  Statistic,
  Button,
  Table,
  Tooltip,
  Skeleton,
} from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  PieController,
  BarController,
  LineController,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import {
  fetchAllRemovebgOrders,
  aggregateFeatureUsage,
  fetchActiveOrganizations,
} from "../../graphql/controllers/fetchAICredits";
import {
  AI_STATISTICS_FEATURES,
  normalizeAiStatisticsFeature,
} from "./aiStatisticsFeatures";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  PieController,
  BarController,
  LineController
);

const { Title: AntTitle } = Typography;

// Chart colors - pastel/professional palette
const CHART_COLORS = [
  "#3D5864", // Primary brand color
  "#66A0BC",
  "#91C6E7",
  "#B6E3FF",
  "#D8C3A5",
  "#E98074",
  "#E85A4F",
  "#8E8D8A",
  "#9BC53D",
  "#5D5C61",
];

const CreditiAICharts = ({ embedded = false }) => {
  // Single state object to manage all component data atomically
  const [componentData, setComponentData] = useState({
    loading: true,
    filterLoading: false,
    loadingProgress: { fetched: 0, total: 0, percentage: 0 },
    featureUsageData: [],
    featureTimelineData: {},
    currentPeriodData: [],
    topOrganizations: [],
    isDataReady: false
  });
  
  
  // Cache for fetched orders organized by date ranges
  const ordersCache = useRef(new Map()); // Map of dateRangeKey -> orders
  const cacheTimestamps = useRef(new Map()); // Map of dateRangeKey -> timestamp
  
  // Track the overall date range we have cached
  const cachedDateRange = useRef({ start: null, end: null });

  // State for the demo org inclusion checkbox
  const [includeDemoOrg, setIncludeDemoOrg] = useState(false);
  
  // State to trigger cache refresh
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Demo organization ID to filter out
  const DEMO_ORG_ID = "PNgeKjwe16";

  const predefinedFeatures = AI_STATISTICS_FEATURES;

  // Set default date range to last 7 days (matching the default button)
  const defaultEndDate = moment();
  const defaultStartDate = moment().subtract(7, "days").startOf("day");

  const [dateRange, setDateRange] = useState([
    defaultStartDate,
    defaultEndDate,
  ]); // [startDate, endDate]

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  // Refs for chart instances
  const pieChartRef = useRef(null);
  
  // Ref to track if we're currently fetching
  const isFetchingRef = useRef(false);
  const fetchIdRef = useRef(0);

  // Function to process orders into a timeline format for each feature
  // Wrapped in useCallback to prevent re-creation on every render
  const processOrdersForTimeline = useCallback(
    (orders, dateStart, dateEnd) => {
      // Initialize data structure to hold feature timeline data
      const timelineData = {};

      // Initialize for predefined features
      predefinedFeatures.forEach((feature) => {
        timelineData[feature] = {};
      });

      // Convert date range to ISO strings for comparison
      const startDate = dateStart
        ? dateStart.startOf("day").toISOString()
        : null;
      const endDate = dateEnd ? dateEnd.endOf("day").toISOString() : null;

      // Process each order
      orders.forEach((order) => {
        if (!order.createdAt || !order.feature) return;

        // Skip orders outside the date range
        const orderDate = new Date(order.createdAt).toISOString();
        if (startDate && orderDate < startDate) return;
        if (endDate && orderDate > endDate) return;

        const dateFormatted = moment(order.createdAt).format("YYYY-MM-DD");
        const feature = order.feature;

        // Skip if not in predefined features
        if (!predefinedFeatures.includes(feature)) return;

        // Initialize date for feature if not exist
        if (!timelineData[feature][dateFormatted]) {
          timelineData[feature][dateFormatted] = 0;
        }

        // Only count negative credits (consumed) and skip positive values (top ups)
        // Sum up credits used (negative values only)
        const freeUsage =
          order.freeCredits && order.freeCredits < 0
            ? Math.abs(order.freeCredits)
            : 0;
        const paidUsage =
          order.paidCredits && order.paidCredits < 0
            ? Math.abs(order.paidCredits)
            : 0;
        timelineData[feature][dateFormatted] += freeUsage + paidUsage;
      });

      return timelineData;
    },
    [predefinedFeatures]
  );

  // Define handleQuickDateFilter early to avoid 'used before defined' warning
  const handleQuickDateFilter = useCallback(
    (period) => {
      let today = moment();
      let startDate;

      switch (period) {
        case "today":
          startDate = moment().startOf("day");
          break;
        case "7days":
          startDate = moment().subtract(7, "days").startOf("day");
          break;
        case "30days":
          startDate = moment().subtract(30, "days").startOf("day");
          break;
        case "3months":
          startDate = moment().subtract(3, "months").startOf("day");
          break;
        case "12months":
          startDate = moment().subtract(12, "months").startOf("day");
          break;
        case "lastYear":
          startDate = moment().subtract(1, "year").startOf("year");
          today = moment().subtract(1, "year").endOf("year");
          break;
        default:
          startDate = moment().subtract(30, "days").startOf("day");
      }

      // Set date range first
      setDateRange([startDate, today]);

      // We intentionally don't include fetchUsageData in this callback
      // since it would create a dependency cycle with useEffect
    },
    [] // No dependencies needed as it only uses moment and setDateRange (stable setState)
  );

  const fetchUsageData = useCallback(async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    const currentFetchId = ++fetchIdRef.current;
    
    try {
      // Set loading state and ALWAYS clear isDataReady to prevent showing stale data
      // Use flushSync to ensure this update happens immediately
      flushSync(() => {
        setComponentData(prev => ({
          ...prev,
          loading: true,
          filterLoading: true,
          isDataReady: false, // Always set to false to ensure UI shows loading state
          loadingProgress: { fetched: 0, total: 1, percentage: 0 } // Set total to 1 to show progress bar immediately
        }));
      });
      
      const token = await getTokenSilently();
      const now = moment();
      
      // Calculate the required date range based on what's being displayed
      // For the selected date range view
      const viewStartDate = dateRange[0] ? dateRange[0].clone().startOf("day") : now.clone().subtract(7, "days").startOf("day");
      const viewEndDate = dateRange[1] ? dateRange[1].clone().endOf("day") : now.clone().endOf("day");
      
      // ONLY fetch data for the selected view range
      // Period statistics will only show data if it's within the cached range
      const requiredStartDate = viewStartDate;
      const requiredEndDate = viewEndDate;
      
      // Check if we need to fetch any data
      let allOrders = [];
      const forceRefresh = refreshTrigger > 0;
      
      if (forceRefresh) {
        // Clear cache on force refresh
        ordersCache.current.clear();
        cacheTimestamps.current.clear();
        cachedDateRange.current = { start: null, end: null };
      }
      
      // Determine what data we need to fetch
      const needsFetch = !cachedDateRange.current.start || 
                        !cachedDateRange.current.end ||
                        requiredStartDate.isBefore(cachedDateRange.current.start) ||
                        requiredEndDate.isAfter(cachedDateRange.current.end) ||
                        (cacheTimestamps.current.size > 0 && 
                         moment().diff(moment(Array.from(cacheTimestamps.current.values())[0]), 'minutes') > 5);
      
      if (needsFetch) {
        // Determine the exact range to fetch
        let fetchStartDate = requiredStartDate;
        let fetchEndDate = requiredEndDate;
        
        // If we have cached data, only fetch the missing parts
        if (cachedDateRange.current.start && !forceRefresh) {
          if (requiredStartDate.isBefore(cachedDateRange.current.start)) {
            // Need earlier data
            fetchEndDate = moment(cachedDateRange.current.start).subtract(1, 'day');
          } else if (requiredEndDate.isAfter(cachedDateRange.current.end)) {
            // Need later data
            fetchStartDate = moment(cachedDateRange.current.end).add(1, 'day');
          }
        }
        
        // Fetch the missing data with progress tracking
        const { orders: newOrders } = await fetchAllRemovebgOrders(
          token, 
          isProd, 
          fetchStartDate.toISOString(), 
          fetchEndDate.toISOString(),
          (progress) => {
            // Only update progress if this is still the current fetch
            if (currentFetchId === fetchIdRef.current) {
              setComponentData(prev => ({
                ...prev,
                loadingProgress: progress
              }));
            }
          }
        );
        
        // Merge with existing cache
        const existingOrders = Array.from(ordersCache.current.values()).flat();
        allOrders = [...existingOrders, ...newOrders];
        
        // Update cache
        const cacheKey = `${fetchStartDate.toISOString()}_${fetchEndDate.toISOString()}`;
        ordersCache.current.set(cacheKey, newOrders);
        cacheTimestamps.current.set(cacheKey, moment().toISOString());
        
        // Update cached date range
        cachedDateRange.current = {
          start: cachedDateRange.current.start && !forceRefresh
            ? moment.min(moment(cachedDateRange.current.start), fetchStartDate)
            : fetchStartDate,
          end: cachedDateRange.current.end && !forceRefresh
            ? moment.max(moment(cachedDateRange.current.end), fetchEndDate)
            : fetchEndDate
        };
      } else {
        allOrders = Array.from(ordersCache.current.values()).flat();
      }

      // Filter orders for the selected date range
      let filteredOrders = [...allOrders];
      
      if (dateRange[0] && dateRange[1]) {
        const startDate = dateRange[0].startOf("day").toISOString();
        const endDate = dateRange[1].endOf("day").toISOString();
        
        filteredOrders = filteredOrders.filter((order) => {
          const orderDate = new Date(order.createdAt).toISOString();
          return orderDate >= startDate && orderDate <= endDate;
        });
      }
      
      // Filter out demo organization orders if the checkbox is not checked
      if (!includeDemoOrg) {
        filteredOrders = filteredOrders.filter(
          (order) => order.orgId !== DEMO_ORG_ID
        );
      }

      filteredOrders = filteredOrders.map((order) => ({
        ...order,
        feature: normalizeAiStatisticsFeature(order.feature),
      }));

      // Don't update state yet - collect all data first
      const timelineData = processOrdersForTimeline(
        filteredOrders,
        dateRange[0],
        dateRange[1]
      );

      // Remove period statistics calculation - no longer needed
      /*
      const calculatePeriodStatistics = () => {
        const now = moment();
        
        // Helper function to filter orders by date range
        const filterOrdersByDateRange = (orders, startDate, endDate) => {
          const start = startDate.toISOString();
          const end = endDate.toISOString();
          return orders.filter(order => {
            const orderDate = new Date(order.createdAt).toISOString();
            return orderDate >= start && orderDate <= end;
          });
        };
        
        // Helper function to check if we have data for a period
        const hasDataForPeriod = (startDate, endDate) => {
          return cachedDateRange.current.start && 
                 cachedDateRange.current.end &&
                 startDate.isSameOrAfter(cachedDateRange.current.start) &&
                 endDate.isSameOrBefore(cachedDateRange.current.end);
        };
        
        // Filter demo org if needed
        const baseOrders = !includeDemoOrg 
          ? allOrders.filter(order => order.orgId !== DEMO_ORG_ID)
          : allOrders;
        
        // Filter orders for different time periods using the cached data
        const todayOrders = filterOrdersByDateRange(baseOrders, now.clone().startOf("day"), now.clone().endOf("day"));
        const yesterdayOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(1, "day").startOf("day"), now.clone().subtract(1, "day").endOf("day"));
        const last7DaysOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(7, "days").startOf("day"), now.clone().endOf("day"));
        const previous7DaysOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(14, "days").startOf("day"), now.clone().subtract(7, "days").endOf("day"));
        const last30DaysOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(30, "days").startOf("day"), now.clone().endOf("day"));
        const previous30DaysOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(60, "days").startOf("day"), now.clone().subtract(30, "days").endOf("day"));
        const last3MonthsOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(3, "months").startOf("day"), now.clone().endOf("day"));
        const previous3MonthsOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(6, "months").startOf("day"), now.clone().subtract(3, "months").endOf("day"));
        const last12MonthsOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(12, "months").startOf("day"), now.clone().endOf("day"));
        const previous12MonthsOrders = filterOrdersByDateRange(baseOrders, now.clone().subtract(24, "months").startOf("day"), now.clone().subtract(12, "months").endOf("day"));

        // Helper function to calculate credits for an array of orders
        const calculateCredits = (orders) => {
          return orders.reduce((sum, order) => {
            const freeUsage = order.freeCredits && order.freeCredits < 0 ? Math.abs(order.freeCredits) : 0;
            const paidUsage = order.paidCredits && order.paidCredits < 0 ? Math.abs(order.paidCredits) : 0;
            return sum + freeUsage + paidUsage;
          }, 0);
        };

        // Calculate credits for each period (only if data is available)
        const todayCredits = hasDataForPeriod(now.clone().startOf("day"), now.clone().endOf("day")) 
          ? calculateCredits(todayOrders) : null;
        const yesterdayCredits = hasDataForPeriod(now.clone().subtract(1, "day").startOf("day"), now.clone().subtract(1, "day").endOf("day"))
          ? calculateCredits(yesterdayOrders) : null;
        const last7DaysCredits = hasDataForPeriod(now.clone().subtract(7, "days").startOf("day"), now.clone().endOf("day"))
          ? calculateCredits(last7DaysOrders) : null;
        const previous7DaysCredits = hasDataForPeriod(now.clone().subtract(14, "days").startOf("day"), now.clone().subtract(7, "days").endOf("day"))
          ? calculateCredits(previous7DaysOrders) : null;
        const last30DaysCredits = hasDataForPeriod(now.clone().subtract(30, "days").startOf("day"), now.clone().endOf("day"))
          ? calculateCredits(last30DaysOrders) : null;
        const previous30DaysCredits = hasDataForPeriod(now.clone().subtract(60, "days").startOf("day"), now.clone().subtract(30, "days").endOf("day"))
          ? calculateCredits(previous30DaysOrders) : null;
        const last3MonthsCredits = hasDataForPeriod(now.clone().subtract(3, "months").startOf("day"), now.clone().endOf("day"))
          ? calculateCredits(last3MonthsOrders) : null;
        const previous3MonthsCredits = hasDataForPeriod(now.clone().subtract(6, "months").startOf("day"), now.clone().subtract(3, "months").endOf("day"))
          ? calculateCredits(previous3MonthsOrders) : null;
        const last12MonthsCredits = hasDataForPeriod(now.clone().subtract(12, "months").startOf("day"), now.clone().endOf("day"))
          ? calculateCredits(last12MonthsOrders) : null;
        const previous12MonthsCredits = hasDataForPeriod(now.clone().subtract(24, "months").startOf("day"), now.clone().subtract(12, "months").endOf("day"))
          ? calculateCredits(previous12MonthsOrders) : null;

        // Calculate percentage changes
        const calculatePercentageChange = (current, previous) => {
          if (current === null || previous === null) return null;
          if (previous === 0) return current > 0 ? 100 : 0;
          return ((current - previous) / previous) * 100;
        };

        // Store these in state for display
        setTotalPeriodCredits({
          today: todayCredits,
          last7Days: last7DaysCredits,
          last30Days: last30DaysCredits,
          last3Months: last3MonthsCredits,
          last12Months: last12MonthsCredits,
        });
        
        // Store comparisons
        setPeriodComparisons({
          today: {
            previous: yesterdayCredits,
            percentage: calculatePercentageChange(todayCredits, yesterdayCredits),
          },
          last7Days: {
            previous: previous7DaysCredits,
            percentage: calculatePercentageChange(last7DaysCredits, previous7DaysCredits),
          },
          last30Days: {
            previous: previous30DaysCredits,
            percentage: calculatePercentageChange(last30DaysCredits, previous30DaysCredits),
          },
          last3Months: {
            previous: previous3MonthsCredits,
            percentage: calculatePercentageChange(last3MonthsCredits, previous3MonthsCredits),
          },
          last12Months: {
            previous: previous12MonthsCredits,
            percentage: calculatePercentageChange(last12MonthsCredits, previous12MonthsCredits),
          },
        });
      };
      */
      
      // Period statistics removed - now showing only selected period stats

      // Aggregate usage data by feature
      const usageByFeature = aggregateFeatureUsage(filteredOrders);

      // Sort by total credits used, descending
      usageByFeature.sort((a, b) => b.totalCredits - a.totalCredits);
      
      // Calculate top organizations
      const topOrgs = calculateTopOrganizationsData(filteredOrders);
      
      // Fetch active organizations and calculate AI usage stats
      let activeOrgsStats = {
        totalActive: 0,
        withAI: 0,
        withoutAI: 0,
        percentageWithAI: 0,
        percentageWithoutAI: 0
      };
      
      try {
        const activeOrgs = await fetchActiveOrganizations(token, isProd);
        const totalActive = activeOrgs.length;
        
        // Get unique org IDs that have used AI in the selected date range
        const orgsWithAI = new Set();
        filteredOrders.forEach(order => {
          if (order.orgId) {
            orgsWithAI.add(order.orgId);
          }
        });
        
        // Count how many active orgs have used AI
        const activeOrgsWithAI = activeOrgs.filter(org => orgsWithAI.has(org.objectId)).length;
        const activeOrgsWithoutAI = totalActive - activeOrgsWithAI;
        
        activeOrgsStats = {
          totalActive,
          withAI: activeOrgsWithAI,
          withoutAI: activeOrgsWithoutAI,
          percentageWithAI: totalActive > 0 ? (activeOrgsWithAI / totalActive) * 100 : 0,
          percentageWithoutAI: totalActive > 0 ? (activeOrgsWithoutAI / totalActive) * 100 : 0
        };
      } catch (error) {
        console.error('Error fetching active organizations stats:', error);
      }

      // Only update if this is still the current fetch
      if (currentFetchId === fetchIdRef.current) {
        // Store the complete data update
        const completeUpdate = {
          loading: false,
          filterLoading: false,
          loadingProgress: { fetched: filteredOrders.length, total: filteredOrders.length, percentage: 100 },
          featureUsageData: usageByFeature,
          featureTimelineData: timelineData,
          currentPeriodData: filteredOrders,
          topOrganizations: topOrgs,
          activeOrgsStats,
          isDataReady: true
        };
        
        // Update state with complete data
        setComponentData(completeUpdate);
      }
    } catch (error) {
      console.error("Error fetching usage data:", error);
      // Only update if this is still the current fetch
      if (currentFetchId === fetchIdRef.current) {
        setComponentData(prev => ({
          ...prev,
          loading: false,
          filterLoading: false,
          isDataReady: prev.isDataReady // Keep existing data ready state
        }));
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [
    getTokenSilently,
    isProd,
    dateRange,
    includeDemoOrg,
    processOrdersForTimeline,
    refreshTrigger,
  ]);
  
  // Function to calculate top organizations data
  const calculateTopOrganizationsData = (orders) => {
    const orgUsageMap = {};
    
    // Aggregate credit usage by organization
    orders.forEach((order) => {
      const orgId = order.orgId;
      const orgName = order.orgName || 'Unknown';
      
      if (!orgUsageMap[orgId]) {
        orgUsageMap[orgId] = {
          orgId,
          orgName,
          creditsUsed: 0,
          creditsSubscription: 0,
          creditsAddon: 0,
        };
      }
      
      // Count negative values as usage
      const freeUsage = order.freeCredits && order.freeCredits < 0 ? Math.abs(order.freeCredits) : 0;
      const paidUsage = order.paidCredits && order.paidCredits < 0 ? Math.abs(order.paidCredits) : 0;
      orgUsageMap[orgId].creditsUsed += freeUsage + paidUsage;
      
      // For positive credit values (credits received), check if they are paid and the note
      if (order.isPaid === true && (order.freeCredits > 0 || order.paidCredits > 0)) {
        const totalPositive = (order.freeCredits > 0 ? order.freeCredits : 0) + 
                             (order.paidCredits > 0 ? order.paidCredits : 0);
        
        // Check if it's an addon purchase based on the note field
        if (order.originalNote === "Acquisto Crediti IA") {
          orgUsageMap[orgId].creditsAddon += totalPositive;
        } else {
          // Otherwise it's subscription credits
          orgUsageMap[orgId].creditsSubscription += totalPositive;
        }
      }
    });
    
    // Convert to array, calculate delta and totals, and sort by credits used
    const orgArray = Object.values(orgUsageMap)
      .map(org => ({
        ...org,
        creditsReceived: org.creditsSubscription + org.creditsAddon,
        delta: (org.creditsSubscription + org.creditsAddon) - org.creditsUsed,
      }))
      .sort((a, b) => b.creditsUsed - a.creditsUsed)
      .slice(0, 20); // Get top 20
    
    return orgArray;
  };
  

  // No need to set filter on mount since default is already 7 days
  // This was causing double fetch on initial load
  // useEffect(() => {
  //   handleQuickDateFilter("7days");
  // }, [handleQuickDateFilter]);

  // Fetch usage data whenever dateRange or includeDemoOrg changes
  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  // Prepare data for pie chart
  const pieChartData = useMemo(() => {
    // Calculate total credits for percentage calculation
    const totalCredits = componentData.featureUsageData.reduce(
      (acc, item) => acc + item.totalCredits,
      0
    );

    return {
      labels: componentData.featureUsageData.map((item) => {
        const percentage =
          totalCredits > 0
            ? ((item.totalCredits / totalCredits) * 100).toFixed(1)
            : 0;
        return `${item.feature} (${percentage}%)`;
      }),
      datasets: [
        {
          data: componentData.featureUsageData.map((item) => item.totalCredits),
          backgroundColor: CHART_COLORS,
          borderWidth: 1,
        },
      ],
    };
  }, [componentData.featureUsageData]);

  // Determine the subtitle based on date range
  const getDateRangeSubtitle = () => {
    if (dateRange[0] && dateRange[1]) {
      return `Periodo: ${dateRange[0].format(
        "DD/MM/YYYY"
      )} - ${dateRange[1].format("DD/MM/YYYY")}`;
    }
    return "";
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Distribuzione Utilizzo Crediti per Strumento AI",
        padding: {
          bottom: 10,
        },
      },
      subtitle: {
        display: !!(dateRange[0] && dateRange[1]),
        text: getDateRangeSubtitle(),
        font: {
          size: 12,
          style: "italic",
        },
        padding: {
          bottom: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} crediti (${percentage}%)`;
          },
        },
      },
    },
  };

  // Line chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        // Using category scale instead of time scale
        type: "category",
        title: {
          display: true,
          text: "Data",
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Crediti Utilizzati",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false, // Disable main title since it's redundant with the card title
        text: "Andamento utilizzo nel tempo",
        padding: {
          bottom: 10,
        },
      },
      subtitle: {
        display: !!(dateRange[0] && dateRange[1]),
        text: getDateRangeSubtitle(),
        font: {
          size: 12,
          style: "italic",
        },
        padding: {
          bottom: 5,
        },
      },
    },
  };

  // Function to prepare aggregated line chart data for all features combined
  const prepareAggregatedLineChartData = () => {
    // Aggregate all features' data for credits used
    const aggregatedData = {};
    const subscriptionData = {};
    const addonData = {};
    
    // Sum up all features for each date (credits used)
    predefinedFeatures.forEach((feature) => {
      const featureData = componentData.featureTimelineData[feature] || {};
      Object.entries(featureData).forEach(([date, value]) => {
        if (!aggregatedData[date]) {
          aggregatedData[date] = 0;
        }
        aggregatedData[date] += value;
      });
    });
    
    // Calculate subscription and addon credits by date
    componentData.currentPeriodData.forEach((order) => {
      if (!order.createdAt) return;
      const date = moment(order.createdAt).format("YYYY-MM-DD");
      
      // Initialize date entries if not exist
      if (!subscriptionData[date]) subscriptionData[date] = 0;
      if (!addonData[date]) addonData[date] = 0;
      
      // For positive credit values (credits received), check if they are paid
      if (order.isPaid === true && (order.freeCredits > 0 || order.paidCredits > 0)) {
        const totalPositive = (order.freeCredits > 0 ? order.freeCredits : 0) + 
                             (order.paidCredits > 0 ? order.paidCredits : 0);
        
        // Check if it's an addon purchase based on the note field
        if (order.originalNote === "Acquisto Crediti IA") {
          addonData[date] += totalPositive;
        } else {
          // Otherwise it's subscription credits
          subscriptionData[date] += totalPositive;
        }
      }
    });

    // Generate all dates in the range
    const allDates = [];
    const dateToFullDate = {}; // Map formatted dates back to full dates
    if (dateRange[0] && dateRange[1]) {
      let currentDate = dateRange[0].clone();
      const endDate = dateRange[1].clone();

      while (currentDate <= endDate) {
        const fullDate = currentDate.format("YYYY-MM-DD");
        allDates.push(fullDate);
        currentDate = currentDate.clone().add(1, "days");
      }
    }

    // Format dates and get values
    const formattedDates = allDates.map((date) => {
      const momentDate = moment(date);
      const formatted = momentDate.format("ddd DD/MM");
      dateToFullDate[formatted] = date;
      return formatted;
    });

    const values = allDates.map((date) => aggregatedData[date] || 0);
    
    // Get values for subscription and addon lines
    const subscriptionValues = allDates.map(
      (date) => subscriptionData[date] || 0
    );
    const addonValues = allDates.map(
      (date) => addonData[date] || 0
    );

    return {
      labels: formattedDates,
      datasets: [
        {
          label: "Crediti Utilizzati",
          data: values,
          borderColor: "#cf1322", // RED for credits used
          backgroundColor: "rgba(207, 19, 34, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#cf1322",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
        {
          label: "Crediti in Abbonamento Erogati",
          data: subscriptionValues,
          borderColor: "#3f8600", // GREEN for subscription credits
          backgroundColor: "rgba(63, 134, 0, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#3f8600",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
        {
          label: "Crediti Acquistati",
          data: addonValues,
          borderColor: "#1890ff", // BLUE for addon credits
          backgroundColor: "rgba(24, 144, 255, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#1890ff",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
      ],
      dateMapping: dateToFullDate, // Include mapping for click handler
      fullDates: allDates, // Include full dates for click handler
    };
  };

  // Function to prepare line chart data for a specific feature
  const prepareLineChartData = (feature) => {
    const timelineData = componentData.featureTimelineData[feature] || {};

    // Generate all dates in the range to ensure we have all days (even those with no data)
    const allDates = [];
    if (dateRange[0] && dateRange[1]) {
      let currentDate = dateRange[0].clone();
      const endDate = dateRange[1].clone();

      while (currentDate <= endDate) {
        allDates.push(currentDate.format("YYYY-MM-DD"));
        currentDate = currentDate.clone().add(1, "days");
      }
    } else {
      // If no date range, use the available dates from the data
      Object.keys(timelineData)
        .sort()
        .forEach((date) => {
          allDates.push(date);
        });
    }

    // Format dates to be more readable, including day of the week
    const formattedDates = allDates.map((date) => {
      const momentDate = moment(date);
      return momentDate.format("ddd DD/MM"); // 'ddd' adds abbreviated day name (Mon, Tue, etc.)
    });

    // Get values for each date, using 0 for missing data
    const values = allDates.map((date) => timelineData[date] || 0);

    return {
      labels: formattedDates,
      datasets: [
        {
          label: feature,
          data: values,
          borderColor:
            CHART_COLORS[
              predefinedFeatures.indexOf(feature) % CHART_COLORS.length
            ],
          backgroundColor: `${
            CHART_COLORS[
              predefinedFeatures.indexOf(feature) % CHART_COLORS.length
            ]
          }33`, // Add transparency
          borderWidth: 2,
          fill: true,
          tension: 0.4, // Smooth curve
        },
      ],
    };
  };

  // Function to force refresh the cache
  const handleForceRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };
  
  // Helper function to format credit values
  const formatCreditValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };
  
  return (
    <div style={{ padding: embedded ? 0 : "20px", position: "relative" }}>
      {/* Loading overlay */}
      {componentData.filterLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Space direction="vertical" align="center" size="large">
            <Spin size="large" />
            <div style={{ textAlign: 'center' }}>
              <Typography.Text style={{ fontSize: '16px', display: 'block', marginBottom: '8px' }}>
                Caricamento dati in corso...
              </Typography.Text>
              {componentData.loadingProgress.total > 0 || componentData.loadingProgress.percentage > 0 ? (
                <>
                  <Typography.Text style={{ fontSize: '14px', color: '#666' }}>
                    {componentData.loadingProgress.fetched.toLocaleString()} di {componentData.loadingProgress.total.toLocaleString()} record
                  </Typography.Text>
                  <div style={{ 
                    width: '300px', 
                    height: '20px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '10px', 
                    overflow: 'hidden',
                    margin: '8px auto'
                  }}>
                    <div style={{ 
                      width: `${componentData.loadingProgress.percentage}%`, 
                      height: '100%', 
                      backgroundColor: '#1890ff',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  <Typography.Text style={{ fontSize: '18px', fontWeight: 'bold' }}>
                    {componentData.loadingProgress.percentage}%
                  </Typography.Text>
                </>
              ) : (
                <Typography.Text style={{ fontSize: '14px', color: '#666' }}>
                  Elaborazione dati...
                </Typography.Text>
              )}
            </div>
          </Space>
        </div>
      )}
      
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {!embedded && (
            <AntTitle level={2} style={{ margin: 0 }}>Grafici Crediti AI</AntTitle>
          )}
        </Col>
        <Col>
          <Tooltip title="Ricarica i dati dal server">
            <Button 
              icon={<ReloadOutlined spin={componentData.loading} />} 
              onClick={handleForceRefresh}
              loading={componentData.loading}
            >
              Aggiorna dati
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Distribuzione per AI Tool">
            <div style={{ height: 400, position: "relative" }}>
              {componentData.loading || componentData.filterLoading || !componentData.isDataReady ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (componentData.isDataReady && componentData.featureUsageData.length > 0) ? (
                <Chart
                  ref={pieChartRef}
                  type="pie"
                  data={pieChartData}
                  options={chartOptions}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography.Text type="secondary">
                    Nessun dato disponibile
                  </Typography.Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Space direction="horizontal" size="small" wrap>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(moment().startOf("day"), "day")
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("today")}
                >
                  Oggi
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(moment().subtract(7, "days").startOf("day"), "day")
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("7days")}
                >
                  Ultimi 7 giorni
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(30, "days").startOf("day"),
                      "day"
                    )
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("30days")}
                >
                  Ultimi 30 giorni
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(3, "months").startOf("day"),
                      "day"
                    )
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("3months")}
                >
                  Ultimi 3 mesi
                </Button>
                <Button
                  size="small"
                  type={
                    dateRange[0] &&
                    dateRange[0].isSame(
                      moment().subtract(12, "months").startOf("day"),
                      "day"
                    )
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleQuickDateFilter("12months")}
                >
                  Ultimi 12 mesi
                </Button>
              </Space>
              <Space align="center">
                  <input
                    type="checkbox"
                    id="includeDemoOrg"
                    checked={includeDemoOrg}
                    onChange={(e) => setIncludeDemoOrg(e.target.checked)}
                  />
                  <label htmlFor="includeDemoOrg">
                    <Typography.Text>Includi org demo</Typography.Text>
                  </label>
                </Space>
            </Space>
          </Card>

          <Divider style={{ margin: "24px 0" }} />

          <Card title="Statistiche Periodo Selezionato">
            {componentData.loading || componentData.filterLoading || !componentData.isDataReady ? (
              <>
                <Skeleton.Input active size="small" style={{ width: 200, marginBottom: 16 }} />
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <div>
                      <Typography.Text type="secondary">Crediti Utilizzati</Typography.Text>
                      <Skeleton.Input active size="large" style={{ width: '100%', height: 40, marginTop: 8 }} />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div>
                      <Typography.Text type="secondary">Crediti in Abbonamento</Typography.Text>
                      <Skeleton.Input active size="large" style={{ width: '100%', height: 40, marginTop: 8 }} />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div>
                      <Typography.Text type="secondary">Crediti Acquistati</Typography.Text>
                      <Skeleton.Input active size="large" style={{ width: '100%', height: 40, marginTop: 8 }} />
                    </div>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Typography.Title level={5} style={{ marginBottom: 16 }}>
                  {getDateRangeSubtitle()}
                </Typography.Title>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Crediti Utilizzati"
                      value={componentData.currentPeriodData.reduce((sum, order) => {
                        const freeUsage = order.freeCredits && order.freeCredits < 0 ? Math.abs(order.freeCredits) : 0;
                        const paidUsage = order.paidCredits && order.paidCredits < 0 ? Math.abs(order.paidCredits) : 0;
                        return sum + freeUsage + paidUsage;
                      }, 0)}
                      formatter={formatCreditValue}
                      valueStyle={{ fontSize: '24px', color: '#cf1322' }}
                      prefix={<ArrowDownOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Crediti in Abbonamento"
                      value={componentData.currentPeriodData.reduce((sum, order) => {
                        // For positive credit values (credits received), check if they are paid and NOT addon
                        if (order.isPaid === true && (order.freeCredits > 0 || order.paidCredits > 0)) {
                          const totalPositive = (order.freeCredits > 0 ? order.freeCredits : 0) + 
                                               (order.paidCredits > 0 ? order.paidCredits : 0);
                          // If it's NOT an addon purchase, it's subscription credits
                          if (order.originalNote !== "Acquisto Crediti IA") {
                            return sum + totalPositive;
                          }
                        }
                        return sum;
                      }, 0)}
                      formatter={formatCreditValue}
                      valueStyle={{ fontSize: '24px', color: '#3f8600' }}
                      prefix={<ArrowUpOutlined />}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Crediti Acquistati"
                      value={componentData.currentPeriodData.reduce((sum, order) => {
                        // For positive credit values (credits received), check if they are paid and ARE addon
                        if (order.isPaid === true && (order.freeCredits > 0 || order.paidCredits > 0)) {
                          const totalPositive = (order.freeCredits > 0 ? order.freeCredits : 0) + 
                                               (order.paidCredits > 0 ? order.paidCredits : 0);
                          // Check if it's an addon purchase based on the note field
                          if (order.originalNote === "Acquisto Crediti IA") {
                            return sum + totalPositive;
                          }
                        }
                        return sum;
                      }, 0)}
                      formatter={formatCreditValue}
                      valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                      prefix={<ArrowUpOutlined />}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Card>
          
          <Card title="Adozione" style={{ marginTop: 16 }}>
            {componentData.loading || componentData.filterLoading || !componentData.isDataReady ? (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div>
                    <Typography.Text type="secondary">Org che usano AI</Typography.Text>
                    <Skeleton.Input active size="large" style={{ width: '100%', height: 40, marginTop: 8 }} />
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <Typography.Text type="secondary">Org che non usano AI</Typography.Text>
                    <Skeleton.Input active size="large" style={{ width: '100%', height: 40, marginTop: 8 }} />
                  </div>
                </Col>
              </Row>
            ) : (
              <>
                <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Su {componentData.activeOrgsStats.totalActive} org attive, che hanno almeno un utilizzo di AI nel periodo selezionato
                </Typography.Text>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="✅"
                      value={componentData.activeOrgsStats.withAI}
                      suffix={
                        <span style={{ fontSize: '16px', color: '#666' }}>
                          ({componentData.activeOrgsStats.percentageWithAI.toFixed(1)}%)
                        </span>
                      }
                      valueStyle={{ fontSize: '24px', color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="😴"
                      value={componentData.activeOrgsStats.withoutAI}
                      suffix={
                        <span style={{ fontSize: '16px', color: '#666' }}>
                          ({componentData.activeOrgsStats.percentageWithoutAI.toFixed(1)}%)
                        </span>
                      }
                      valueStyle={{ fontSize: '24px', color: '#8c8c8c' }}
                    />
                  </Col>
                </Row>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card>
            <Typography.Title level={5}>Andamento Totale Giornaliero</Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Clicca su un punto per filtrare tutti i grafici su quel giorno
            </Typography.Text>
            <div style={{ height: 300, position: "relative", marginTop: 16 }}>
              {componentData.loading || componentData.filterLoading || !componentData.isDataReady ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : dateRange[0] && dateRange[1] ? (
                (() => {
                  const chartData = prepareAggregatedLineChartData();
                  return (
                    <Chart
                      type="line"
                      data={chartData}
                      options={{
                        ...lineChartOptions,
                        interaction: {
                          mode: 'nearest',
                          intersect: false,
                        },
                        plugins: {
                          ...lineChartOptions.plugins,
                          title: {
                            display: false,
                          },
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            ...lineChartOptions.plugins.tooltip,
                            callbacks: {
                              ...lineChartOptions.plugins.tooltip?.callbacks,
                              afterLabel: function() {
                                return 'Clicca per filtrare su questo giorno';
                              },
                            },
                          },
                        },
                        onClick: (event, elements) => {
                          if (elements && elements.length > 0) {
                            const index = elements[0].index;
                            const clickedDate = chartData.fullDates[index];
                            if (clickedDate) {
                              // Set the date range to just this day
                              const selectedDate = moment(clickedDate);
                              setDateRange([selectedDate.clone().startOf('day'), selectedDate.clone().endOf('day')]);
                            }
                          }
                        },
                        onHover: (event, activeElements) => {
                          event.native.target.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
                        },
                      }}
                    />
                  );
                })()
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography.Text type="secondary">
                    Nessun dato disponibile
                  </Typography.Text>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Top 20 Organizzazioni per Utilizzo Crediti" loading={componentData.loading || componentData.filterLoading || !componentData.isDataReady}>
            <Table
              dataSource={componentData.topOrganizations}
              columns={[
                {
                  title: "#",
                  key: "index",
                  width: 50,
                  render: (_, __, index) => index + 1,
                },
                {
                  title: "Organizzazione",
                  dataIndex: "orgName",
                  key: "orgName",
                  ellipsis: true,
                  render: (text, record) => (
                    <Link to={`/orgs/${record.orgId}/ai`}>
                      {text}
                    </Link>
                  ),
                },
                {
                  title: "Crediti Utilizzati",
                  dataIndex: "creditsUsed",
                  key: "creditsUsed",
                  sorter: (a, b) => a.creditsUsed - b.creditsUsed,
                  render: (value) => {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(2)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value.toLocaleString();
                  },
                },
                {
                  title: "Crediti in Abbonamento",
                  dataIndex: "creditsSubscription",
                  key: "creditsSubscription",
                  sorter: (a, b) => a.creditsSubscription - b.creditsSubscription,
                  render: (value) => {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(2)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value.toLocaleString();
                  },
                },
                {
                  title: "Crediti Acquistati",
                  dataIndex: "creditsAddon",
                  key: "creditsAddon",
                  sorter: (a, b) => a.creditsAddon - b.creditsAddon,
                  render: (value) => {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(2)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                    return value.toLocaleString();
                  },
                },
                {
                  title: "Delta",
                  dataIndex: "delta",
                  key: "delta",
                  sorter: (a, b) => a.delta - b.delta,
                  render: (value) => {
                    const formatted = value >= 1000000 
                      ? `${(value / 1000000).toFixed(2)}M`
                      : value >= 1000 || value <= -1000
                      ? `${(value / 1000).toFixed(1)}K`
                      : value.toLocaleString();
                    
                    const color = value >= 0 ? '#3f8600' : '#cf1322';
                    const prefix = value >= 0 ? '+' : '';
                    
                    return (
                      <span style={{ color, fontWeight: 'bold' }}>
                        {prefix}{formatted}
                      </span>
                    );
                  },
                },
              ]}
              pagination={false}
              size="small"
              rowKey="orgId"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {predefinedFeatures.map((feature, index) => (
          <Col xs={24} sm={24} md={12} key={feature}>
            <Card title={feature} loading={componentData.loading || componentData.filterLoading || !componentData.isDataReady}>
              <div style={{ height: 350, position: "relative" }}>
                {componentData.loading || componentData.filterLoading || !componentData.isDataReady ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Spin size="large" />
                  </div>
                ) : dateRange[0] && dateRange[1] ? (
                  <Chart
                    type="line"
                    data={prepareLineChartData(feature)}
                    options={lineChartOptions}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography.Text type="secondary">
                      Nessun dato disponibile
                    </Typography.Text>
                  </div>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default CreditiAICharts;
